import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Target, Activity, Users, LineChart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { ClientDialog } from "@/components/ClientDialog";

function ClientCard({ c, onEdit, onDelete }) {
  return (
    <div className="rounded-xl border bg-white p-6 hover:shadow-md transition-shadow" data-testid={`client-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-secondary text-primary grid place-items-center font-display font-semibold">{c.name.slice(0, 1).toUpperCase()}</div>
          <div>
            <div className="font-display font-semibold text-slate-900">{c.name}</div>
            <div className="text-xs text-slate-500">{c.diagnosis || "No diagnosis listed"}</div>
          </div>
        </div>
        <div className="flex">
          <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="hover:text-red-600" onClick={onDelete} data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="mt-5 space-y-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 mb-2"><Target className="h-3.5 w-3.5" />Goals ({c.goals.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {c.goals.map((g) => <span key={g.id} className="text-xs px-2.5 py-1 rounded-full bg-secondary text-teal-900">{g.name}</span>)}
            {!c.goals.length && <span className="text-xs text-slate-400">None yet</span>}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 mb-2"><Activity className="h-3.5 w-3.5" />Behaviors</div>
          <div className="flex flex-wrap gap-1.5">
            {c.behaviors.map((b) => <span key={b.id} className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800">{b.name}</span>)}
            {!c.behaviors.length && <span className="text-xs text-slate-400">None yet</span>}
          </div>
        </div>
      </div>
      <Button asChild variant="outline" size="sm" className="mt-5 w-full rounded-full" data-testid={`client-progress-${c.id}`}>
        <Link to={`/clients/${c.id}/progress`}><LineChart className="h-4 w-4 mr-2 text-primary" />View progress</Link>
      </Button>
    </div>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const onSaved = (c) => setClients((cs) => (cs.some((x) => x.id === c.id) ? cs.map((x) => (x.id === c.id ? c : x)) : [...cs, c]));
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Their notes are kept.`)) return;
    try { await api.delete(`/clients/${c.id}`); setClients((cs) => cs.filter((x) => x.id !== c.id)); toast.success("Client deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Clients & goals</h1>
          <p className="text-slate-500 mt-1">Goals and behaviors load into every new note automatically.</p>
        </div>
        <Button className="rounded-full h-11 px-6" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-client-button"><Plus className="h-4 w-4 mr-2" />Add client</Button>
      </div>
      {clients && clients.length === 0 ? (
        <div className="rounded-2xl border border-dashed bg-white p-12 text-center" data-testid="clients-empty-state">
          <Users className="h-8 w-8 mx-auto text-primary/60" />
          <h3 className="mt-4 text-lg font-semibold">No clients yet</h3>
          <p className="text-sm text-slate-500 mt-1">Add a client with their goals to prefill your session notes.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 stagger" data-testid="clients-list">
          {(clients || []).map((c) => <ClientCard key={c.id} c={c} onEdit={() => { setEditing(c); setOpen(true); }} onDelete={() => remove(c)} />)}
        </div>
      )}
      <ClientDialog open={open} onOpenChange={setOpen} client={editing} onSaved={onSaved} />
    </div>
  );
}
